import { ApiError } from './errors';
import { mockRequest } from './mock';
import {
  sortMessages,
  toAiSettings,
  toAiTestResult,
  toAuditEvent,
  toMediaUpload,
  toSettings,
  toContact,
  toContactDetail,
  toContactListItem,
  toConversation,
  toDoc,
  toFaq,
  toMessage,
  toPreview,
  toTestModeState,
  toTicket,
  toUser,
  toWaStatus,
  unavailableWaStatus
} from './adapt';
import type {
  ApiAiSettingsDto,
  ApiAiTestResult,
  ApiAuditEvent,
  ApiMediaUpload,
  ApiSettings,
  ApiTotpEnable,
  ApiTotpSetup,
  ApiContact,
  ApiContactDetail,
  ApiConversation,
  ApiConversationDetail,
  ApiDoc,
  ApiFaq,
  ApiLoginResponse,
  ApiMe,
  ApiMessage,
  ApiPage,
  ApiPreview,
  ApiTestModeState,
  ApiTicket,
  ApiWaStatus
} from './apiTypes';
import type {
  AiSettings,
  AiTestResult,
  AuditEvent,
  Contact,
  Conversation,
  ConversationDetail,
  Doc,
  Faq,
  InboxFilter,
  LoginPasswordResult,
  MediaUpload,
  Message,
  Page,
  PanelConfig,
  Role,
  TestModeState,
  TestPreviewResult,
  Ticket,
  TotpSetup,
  User,
  WaStatus
} from './types';

/**
 * API istemcisi — iki mod:
 *  - VITE_MOCK=1 (dev varsayılanı): lib/mock.ts bellek içi API; ağ çağrısı yok.
 *  - VITE_MOCK=0: same-origin /api (Vite dev'de 3200'e proxy), çerez oturumu + CSRF başlığı.
 * Ekranlar yalnız bu arayüzü görür; ham backend DTO'ları lib/adapt.ts'de UI tipine çevrilir.
 */
export const MOCK_MODE =
  import.meta.env.VITE_MOCK === '1' ||
  (import.meta.env.DEV && import.meta.env.VITE_MOCK !== '0');

export interface MeResult {
  user: User;
  permissions: string[];
}

export interface InboxQuery {
  filter: InboxFilter;
  search: string;
  cursor?: string | null;
  /** "Atanmışım" filtresi için oturumdaki kullanıcı kimliği. */
  userId?: string | null;
}

export interface ApiClient {
  me(): Promise<MeResult>;
  loginPassword(payload: { email: string; password: string }): Promise<LoginPasswordResult>;
  verify2fa(payload: { ticket: string; code: string }): Promise<User>;
  loginSso(role?: Role): Promise<User>;
  logout(): Promise<void>;

  waStatus(): Promise<WaStatus>;
  waPair(payload: { method: 'qr' | 'code'; phone?: string }): Promise<void>;
  waReconnect(): Promise<void>;
  waLogout(): Promise<void>;

  inbox(query: InboxQuery): Promise<Page<Conversation>>;
  conversation(id: string): Promise<ConversationDetail>;
  sendMessage(id: string, body: string, media?: { id?: string; url?: string; mime?: string; name?: string } | null): Promise<Message>;
  addNote(id: string, body: string): Promise<Message>;
  markRead(id: string): Promise<void>;
  aiTakeover(id: string): Promise<Conversation>;
  aiResume(id: string): Promise<Conversation>;
  assign(id: string, userId: string | null): Promise<Conversation>;

  contacts(search: string): Promise<Page<Contact>>;
  contact(id: string): Promise<Contact>;
  contactOptOut(id: string): Promise<Contact>;
  contactBlock(id: string): Promise<Contact>;
  contactNotes(id: string, notes: string): Promise<Contact>;

  tickets(status?: string): Promise<Page<Ticket>>;
  ticket(id: string): Promise<Ticket>;
  ticketReply(id: string, channel: 'whatsapp' | 'email', body: string): Promise<Ticket>;
  ticketClose(id: string): Promise<Ticket>;
  ticketAssign(id: string, userId: string | null): Promise<Ticket>;

  faqs(): Promise<Page<Faq>>;
  saveFaq(faq: Partial<Faq> & { question: string; answer: string }): Promise<Faq>;
  toggleFaq(id: string, active: boolean): Promise<Faq>;
  deleteFaq(id: string): Promise<void>;

  docs(): Promise<Page<Doc>>;
  uploadDoc(file: File): Promise<Doc>;
  reindexDoc(id: string): Promise<Doc>;
  deleteDoc(id: string): Promise<void>;
  confirmDocFaqs(id: string): Promise<Doc>;

  aiSettings(): Promise<AiSettings>;
  saveAiSettings(patch: Partial<AiSettings>): Promise<AiSettings>;
  testAi(question: string): Promise<AiTestResult>;

  testMode(): Promise<TestModeState>;
  testPreview(payload: { text: string; to: string }): Promise<TestPreviewResult>;
  addAllowlist(phone: string): Promise<void>;
  removeAllowlist(phone: string): Promise<void>;
  enterLive(payload: { phone: string; totp: string }): Promise<{ state: TestModeState; warnings: string[] }>;
  enterTest(): Promise<TestModeState>;

  audit(query: AuditQuery): Promise<Page<AuditEvent>>;
  /** GET /api/admin/audit.csv — aynı filtrelerle sunucu tarafı CSV. */
  auditCsv(query: AuditQuery): Promise<{ blob: Blob; filename: string }>;
  settings(): Promise<PanelConfig>;
  saveSettings(config: PanelConfig): Promise<PanelConfig>;

  /** POST /api/media/upload — dosyayı yükler, mesaja eklenebilir referans döner. */
  uploadMedia(file: File): Promise<MediaUpload>;

  totpSetup(): Promise<TotpSetup>;
  totpEnable(code: string): Promise<string[]>;
  regenerateRecoveryCodes(code: string): Promise<string[]>;
}

export interface AuditQuery {
  action?: string;
  actor?: string;
  cursor?: string | null;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Ortak yardımcılar                                                   */
/* ------------------------------------------------------------------ */

function readCookie(name: string): string | null {
  const pattern = new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)');
  const match = document.cookie.match(pattern);
  return match ? decodeURIComponent(match[1]) : null;
}

/** CSRF: backend wp_csrf çerezi yazar; mutasyonlarda başlıkla gönderilir. */
function csrfHeaders(): Record<string, string> {
  const token = readCookie('wp_csrf') || readCookie('csrf') || readCookie('XSRF-TOKEN');
  return token ? { 'X-CSRF-Token': token } : {};
}

interface RequestOptions {
  headers?: Record<string, string>;
}

async function request<T>(method: string, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...csrfHeaders(),
      ...options.headers
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (response.status === 401) {
    // Oturum düştü: AuthProvider dinler ve kullanıcıyı çıkarır.
    window.dispatchEvent(new CustomEvent('wa:unauthorized'));
  }

  if (!response.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(err?.code || 'INTERNAL', err?.message || 'İstek başarısız oldu.', response.status, err?.details);
  }

  return payload as T;
}

function auditParams(query: AuditQuery): URLSearchParams {
  const params = new URLSearchParams({ limit: String(query.limit ?? 50) });
  if (query.action) params.set('action', query.action);
  if (query.actor) params.set('actor', query.actor);
  if (query.cursor) params.set('cursor', query.cursor);
  return params;
}

function notImplemented(feature: string): never {
  throw new ApiError('NOT_IMPLEMENTED', feature + " ucu backend'de henüz yayınlanmadı.", 501);
}

const AGENT_PERMISSIONS = ['inbox', 'tickets', 'contacts'];

/* ------------------------------------------------------------------ */
/* Gerçek API istemcisi                                                */
/* ------------------------------------------------------------------ */

const realClient: ApiClient = {
  async me() {
    const me = await request<ApiMe>('GET', '/api/auth/me');
    return { user: toUser(me.user), permissions: me.permissions ?? AGENT_PERMISSIONS };
  },

  async loginPassword({ email, password }) {
    const res = await request<ApiLoginResponse>('POST', '/api/auth/login', { email, password });
    if (res.twoFactorRequired && res.ticket) {
      return { twoFactorRequired: true, ticket: res.ticket };
    }
    if (!res.user) throw new ApiError('LOGIN_FAILED', 'Giriş yanıtı beklenmedik biçimde geldi.', 500);
    return { twoFactorRequired: false, user: toUser(res.user) };
  },

  async verify2fa({ ticket, code }) {
    const res = await request<ApiLoginResponse>('POST', '/api/auth/2fa/verify', { ticket, code });
    if (!res.user) throw new ApiError('TWOFA_FAILED', '2FA yanıtı beklenmedik biçimde geldi.', 500);
    return toUser(res.user);
  },

  async loginSso() {
    // OAuth2 akışı tam sayfa yönlendirme ile yürür; dönüşte /api/auth/me oturumu görür.
    window.location.assign('/api/auth/sso/start');
    return new Promise<User>(() => undefined);
  },

  logout: () => request<void>('POST', '/api/auth/logout'),

  async waStatus() {
    try {
      const raw = await request<ApiWaStatus>('GET', '/api/wa/status');
      return toWaStatus(raw);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 501)) return unavailableWaStatus();
      throw error;
    }
  },

  async waPair({ method, phone }) {
    try {
      await request<ApiWaStatus>('POST', '/api/wa/pair', method === 'code' ? { method, phoneE164: phone } : { method });
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
        throw new ApiError('NOT_IMPLEMENTED', 'QR/kod ile bağlama ucu henüz yayınlanmadı.', 501);
      }
      throw error;
    }
  },

  async waReconnect() {
    await request<ApiWaStatus>('POST', '/api/wa/reconnect');
  },

  async waLogout() {
    try {
      await request<ApiWaStatus>('POST', '/api/wa/logout');
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
        throw new ApiError('NOT_IMPLEMENTED', 'Oturum kapatma ucu henüz yayınlanmadı.', 501);
      }
      throw error;
    }
  },

  async inbox(query) {
    const params = new URLSearchParams({ limit: '50' });
    if (query.search) params.set('q', query.search);
    if (query.filter === 'mine' && query.userId) params.set('assigneeId', query.userId);
    if (query.filter === 'waiting') params.set('status', 'open');
    if (query.cursor) params.set('cursor', query.cursor);

    const page = await request<ApiPage<ApiConversation>>('GET', '/api/conversations?' + params.toString());
    let items = page.items.map(toConversation);

    // Sunucuda karşılığı olmayan filtreler istemcide daraltılır (liste ≤50).
    if (query.filter === 'waiting') items = items.filter((item) => item.waitingSince !== null);
    if (query.filter === 'ai') items = items.filter((item) => item.aiState === 'ai');
    if (query.filter === 'test') items = items.filter((item) => item.isTest);

    return { items, nextCursor: page.nextCursor };
  },

  async conversation(id) {
    const [detail, messages] = await Promise.all([
      request<ApiConversationDetail>('GET', '/api/conversations/' + id),
      request<ApiPage<ApiMessage>>('GET', '/api/conversations/' + id + '/messages?limit=100')
    ]);
    return {
      conversation: toConversation(detail),
      messages: sortMessages(messages.items),
      contact: toContact(detail.contact)
    };
  },

  async sendMessage(id, body, media) {
    const res = await request<{ message: ApiMessage; outbox: { id: string; status: string; idempotencyKey: string }; queued: boolean }>(
      'POST',
      '/api/conversations/' + id + '/messages',
      media?.id ? { body, mediaId: media.id, mediaName: media.name } : { body },
      { headers: { 'Idempotency-Key': crypto.randomUUID() } }
    );
    const message = toMessage(res.message);
    if (res.outbox?.status === 'blocked') {
      message.status = 'blocked';
      message.blockedReason = 'TEST_MODE_BLOCKED';
    }
    return message;
  },

  async addNote(id, body) {
    const res = await request<{ message?: ApiMessage } | ApiMessage>('POST', '/api/conversations/' + id + '/notes', { body });
    const raw = (res as { message?: ApiMessage }).message ?? (res as ApiMessage);
    const message = toMessage(raw);
    message.isNote = true;
    return message;
  },

  markRead: (id) => request<void>('POST', '/api/conversations/' + id + '/read'),

  async aiTakeover(id) {
    return toConversation(await request<ApiConversation>('POST', '/api/conversations/' + id + '/ai/takeover'));
  },

  async aiResume(id) {
    return toConversation(await request<ApiConversation>('POST', '/api/conversations/' + id + '/ai/resume'));
  },

  async assign(id, userId) {
    return toConversation(await request<ApiConversation>('POST', '/api/conversations/' + id + '/assign', { assigneeId: userId }));
  },

  async contacts(search) {
    const params = new URLSearchParams({ limit: '100' });
    // q boş gönderilemez (zod min(1)); yalnız arama varken eklenir.
    if (search.trim()) params.set('q', search.trim());
    const page = await request<{ items: Array<ApiContact & { summary?: { conversationCount?: number; openConversationCount?: number } }>; nextCursor?: string | null }>(
      'GET',
      '/api/contacts?' + params.toString()
    );
    return { items: (page.items ?? []).map(toContactListItem), nextCursor: page.nextCursor ?? null };
  },

  async contact(id) {
    return toContactDetail(await request<ApiContactDetail>('GET', '/api/contacts/' + id));
  },

  async contactOptOut(id) {
    const res = await request<ApiContact | ApiContactDetail>('POST', '/api/contacts/' + id + '/opt-out', { source: 'panel' });
    return 'contact' in res ? toContactDetail(res) : toContact(res);
  },

  async contactBlock(id) {
    const res = await request<ApiContact | ApiContactDetail>('POST', '/api/contacts/' + id + '/block', { blocked: true });
    return 'contact' in res ? toContactDetail(res) : toContact(res);
  },

  async contactNotes() {
    return notImplemented('Kişi notu güncelleme');
  },

  async tickets(status) {
    const params = new URLSearchParams({ limit: '50' });
    if (status && status !== 'all') {
      params.set('status', status === 'pending' ? 'in_progress' : status === 'resolved' ? 'closed' : status);
    }
    const page = await request<ApiPage<ApiTicket>>('GET', '/api/tickets?' + params.toString());
    return { items: page.items.map(toTicket), nextCursor: page.nextCursor ?? null };
  },

  async ticket(id) {
    return toTicket(await request<ApiTicket>('GET', '/api/tickets/' + id));
  },

  async ticketReply(id, channel, body) {
    return toTicket(await request<ApiTicket>('POST', '/api/tickets/' + id + '/reply', { channel, body }));
  },

  async ticketClose(id) {
    return toTicket(await request<ApiTicket>('POST', '/api/tickets/' + id + '/close'));
  },

  async ticketAssign(id, userId) {
    return toTicket(await request<ApiTicket>('POST', '/api/tickets/' + id + '/assign', { userId }));
  },

  async faqs() {
    const page = await request<{ items: ApiFaq[]; total: number }>('GET', '/api/faqs?limit=200');
    return { items: page.items.map(toFaq), nextCursor: null };
  },

  async saveFaq(faq) {
    const payload = {
      question: faq.question,
      answer: faq.answer,
      category: faq.category || null,
      tags: faq.tags ?? [],
      active: faq.active ?? true
    };
    const saved = faq.id
      ? await request<ApiFaq>('PATCH', '/api/faqs/' + faq.id, payload)
      : await request<ApiFaq>('POST', '/api/faqs', payload);
    return toFaq(saved);
  },

  async toggleFaq(id, active) {
    return toFaq(await request<ApiFaq>('PATCH', '/api/faqs/' + id, { active }));
  },

  async deleteFaq(id) {
    await request<{ deleted: boolean }>('DELETE', '/api/faqs/' + id);
  },

  async docs() {
    const page = await request<{ items: ApiDoc[]; total: number }>('GET', '/api/library?limit=100');
    return { items: page.items.map(toDoc), nextCursor: null };
  },

  async uploadDoc(file) {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('title', file.name);
    const response = await fetch('/api/library/upload', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', ...csrfHeaders() },
      body: form
    });
    const text = await response.text();
    const payload = text
      ? (JSON.parse(text) as
          | ApiDoc
          | { document?: ApiDoc; doc?: ApiDoc }
          | { error?: { message?: string } })
      : null;
    if (!response.ok) {
      const message = (payload as { error?: { message?: string } } | null)?.error?.message;
      throw new ApiError('UPLOAD_FAILED', message || 'Yükleme başarısız oldu.', response.status);
    }
    const wrapped = payload as { document?: ApiDoc; doc?: ApiDoc };
    const doc = wrapped.document ?? wrapped.doc ?? (payload as ApiDoc);
    return toDoc(doc);
  },

  async reindexDoc(id) {
    return toDoc(await request<ApiDoc>('POST', '/api/library/' + id + '/reindex'));
  },

  async deleteDoc(id) {
    await request<{ deleted: boolean }>('DELETE', '/api/library/' + id);
  },

  async confirmDocFaqs(id) {
    const result = await request<{ document?: ApiDoc } | ApiDoc>('POST', '/api/library/' + id + '/approve', {
      approved: true
    });
    const doc = (result as { document?: ApiDoc }).document ?? (result as ApiDoc);
    return doc && 'id' in doc ? toDoc(doc) : notImplemented('Kütüphane onayı');
  },

  async aiSettings() {
    return toAiSettings(await request<ApiAiSettingsDto>('GET', '/api/ai/settings'));
  },

  async saveAiSettings(patch) {
    const body: Record<string, unknown> = {};
    if (patch.model !== undefined) body.model = patch.model;
    if (patch.effort !== undefined) body.effort = patch.effort;
    if (patch.confidenceThreshold !== undefined) body.confidenceThreshold = patch.confidenceThreshold;
    if (patch.dailyBudgetUsd !== undefined) body.dailyBudgetUsd = patch.dailyBudgetUsd;
    if (patch.promptExtra !== undefined) body.promptExtra = patch.promptExtra;
    if (patch.autoReply !== undefined) body.autoReply = patch.autoReply;
    if (patch.maxRepliesPerConversation !== undefined) body.maxRepliesPerConversation = patch.maxRepliesPerConversation;
    return toAiSettings(await request<ApiAiSettingsDto>('PUT', '/api/ai/settings', body));
  },

  async testAi(question) {
    return toAiTestResult(await request<ApiAiTestResult>('POST', '/api/ai/test', { question }));
  },

  async testMode() {
    return toTestModeState(await request<ApiTestModeState>('GET', '/api/test-mode'));
  },

  async testPreview({ text, to }) {
    return toPreview(await request<ApiPreview>('POST', '/api/test-mode/preview', { text, to }), to);
  },

  async addAllowlist(phone) {
    await request('POST', '/api/test-mode/allowlist', { phone });
  },

  async removeAllowlist(phone) {
    await request('DELETE', '/api/test-mode/allowlist', { phone });
  },

  async enterLive({ phone, totp }) {
    const res = await request<{ state: ApiTestModeState; warnings: string[] }>('POST', '/api/test-mode/enter-live', {
      phone,
      totp,
      confirm: true
    });
    return { state: toTestModeState(res.state), warnings: res.warnings ?? [] };
  },

  async enterTest() {
    const res = await request<{ state: ApiTestModeState }>('POST', '/api/test-mode/enter-test');
    return toTestModeState(res.state);
  },

  async audit(query) {
    const page = await request<{ items: ApiAuditEvent[]; nextCursor?: string | null }>(
      'GET',
      '/api/admin/audit?' + auditParams(query).toString()
    );
    return {
      items: (page.items ?? []).map(toAuditEvent),
      nextCursor: page.nextCursor ?? null
    };
  },

  async auditCsv(query) {
    const response = await fetch('/api/admin/audit.csv?' + auditParams(query).toString(), {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...csrfHeaders() }
    });
    if (!response.ok) {
      throw new ApiError('AUDIT_CSV_FAILED', 'CSV indirilemedi.', response.status);
    }
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(disposition);
    return { blob: await response.blob(), filename: match ? match[1] : 'audit.csv' };
  },

  async settings() {
    const payload = await request<{ settings: ApiSettings } | ApiSettings>('GET', '/api/settings');
    return toSettings('settings' in payload ? payload.settings : payload);
  },

  async saveSettings(config) {
    const payload = await request<{ settings: ApiSettings } | ApiSettings>('PUT', '/api/settings', {
      panelName: config.panelName,
      supportEmail: config.supportEmail,
      timezone: config.timezone,
      businessHoursStart: config.businessHoursStart,
      businessHoursEnd: config.businessHoursEnd,
      testRecipients: config.testRecipients
    });
    return toSettings('settings' in payload ? payload.settings : payload);
  },

  async uploadMedia(file) {
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await fetch('/api/media/upload', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', ...csrfHeaders() },
      body: form
    });
    const text = await response.text();
    const payload = text ? (JSON.parse(text) as ApiMediaUpload | { error?: { message?: string } }) : null;
    if (!response.ok) {
      const message = (payload as { error?: { message?: string } } | null)?.error?.message;
      throw new ApiError('UPLOAD_FAILED', message || 'Medya yüklenemedi.', response.status);
    }
    return toMediaUpload(payload as ApiMediaUpload);
  },

  async totpSetup() {
    const raw = await request<ApiTotpSetup>('POST', '/api/auth/totp/setup');
    return { secret: raw.secret, otpauthUrl: raw.otpauthUrl, issuer: raw.issuer || 'Destek Merkezi' };
  },

  async totpEnable(code) {
    const res = await request<ApiTotpEnable>('POST', '/api/auth/totp/enable', { code });
    return res.recoveryCodes ?? [];
  },

  async regenerateRecoveryCodes(code) {
    const res = await request<ApiTotpEnable>('POST', '/api/auth/recovery-codes', { code });
    return res.recoveryCodes ?? [];
  }
};

/* ------------------------------------------------------------------ */
/* Mock istemcisi                                                      */
/* ------------------------------------------------------------------ */

const mockClient: ApiClient = {
  async me() {
    const user = await mockRequest<User>('GET', '/api/auth/me');
    return { user, permissions: user.role === 'owner' ? ['*'] : AGENT_PERMISSIONS };
  },
  async loginPassword({ email, password }) {
    return mockRequest<LoginPasswordResult>('POST', '/api/auth/login', { mode: 'password', email, password });
  },
  async verify2fa({ ticket, code }) {
    return mockRequest<User>('POST', '/api/auth/2fa/verify', { ticket, code });
  },
  async loginSso(role = 'owner') {
    return mockRequest<User>('POST', '/api/auth/login', { mode: 'sso', role });
  },
  logout: () => mockRequest<void>('POST', '/api/auth/logout'),

  waStatus: () => mockRequest<WaStatus>('GET', '/api/wa/status'),
  async waPair(payload) {
    await mockRequest('POST', '/api/wa/pair', payload);
  },
  async waReconnect() {
    await mockRequest('POST', '/api/wa/reconnect');
  },
  async waLogout() {
    await mockRequest('POST', '/api/wa/logout');
  },

  inbox: (query) =>
    mockRequest<Page<Conversation>>(
      'GET',
      '/api/conversations?' + new URLSearchParams({ filter: query.filter, q: query.search }).toString()
    ),
  conversation: (id) => mockRequest<ConversationDetail>('GET', '/api/conversations/' + id),
  sendMessage: (id, body, media) =>
    mockRequest<Message>('POST', '/api/conversations/' + id + '/messages', { body, media }),
  addNote: (id, body) => mockRequest<Message>('POST', '/api/conversations/' + id + '/notes', { body }),
  markRead: (id) => mockRequest<void>('POST', '/api/conversations/' + id + '/read'),
  aiTakeover: (id) => mockRequest<Conversation>('POST', '/api/conversations/' + id + '/ai/takeover'),
  aiResume: (id) => mockRequest<Conversation>('POST', '/api/conversations/' + id + '/ai/resume'),
  assign: (id, userId) => mockRequest<Conversation>('POST', '/api/conversations/' + id + '/assign', { userId }),

  contacts: (search) => mockRequest<Page<Contact>>('GET', '/api/contacts?q=' + encodeURIComponent(search)),
  contact: (id) => mockRequest<Contact>('GET', '/api/contacts/' + id),
  contactOptOut: (id) => mockRequest<Contact>('POST', '/api/contacts/' + id + '/opt-out'),
  contactBlock: (id) => mockRequest<Contact>('POST', '/api/contacts/' + id + '/block'),
  contactNotes: (id, notes) => mockRequest<Contact>('PATCH', '/api/contacts/' + id, { notes }),

  tickets: (status) => mockRequest<Page<Ticket>>('GET', '/api/tickets' + (status && status !== 'all' ? '?status=' + status : '')),
  ticket: (id) => mockRequest<Ticket>('GET', '/api/tickets/' + id),
  ticketReply: (id, channel, body) => mockRequest<Ticket>('POST', '/api/tickets/' + id + '/reply', { channel, body }),
  ticketClose: (id) => mockRequest<Ticket>('POST', '/api/tickets/' + id + '/close'),
  ticketAssign: (id, userId) => mockRequest<Ticket>('POST', '/api/tickets/' + id + '/assign', { userId }),

  faqs: () => mockRequest<Page<Faq>>('GET', '/api/faqs'),
  saveFaq: (faq) => mockRequest<Faq>(faq.id ? 'PATCH' : 'POST', faq.id ? '/api/faqs/' + faq.id : '/api/faqs', faq),
  toggleFaq: (id, active) => mockRequest<Faq>('PATCH', '/api/faqs/' + id, { active }),
  deleteFaq: (id) => mockRequest<void>('DELETE', '/api/faqs/' + id),

  docs: () => mockRequest<Page<Doc>>('GET', '/api/library'),
  uploadDoc: (file) => mockRequest<Doc>('POST', '/api/library/upload', { name: file.name, size: file.size }),
  reindexDoc: (id) => mockRequest<Doc>('POST', '/api/library/' + id + '/reindex'),
  deleteDoc: (id) => mockRequest<void>('DELETE', '/api/library/' + id),
  confirmDocFaqs: (id) => mockRequest<Doc>('POST', '/api/library/' + id + '/confirm'),

  aiSettings: () => mockRequest<AiSettings>('GET', '/api/ai/settings'),
  saveAiSettings: (patch) => mockRequest<AiSettings>('PUT', '/api/ai/settings', patch),
  testAi: (question) => mockRequest<AiTestResult>('POST', '/api/ai/test', { question }),

  testMode: () => mockRequest<TestModeState>('GET', '/api/test-mode'),
  testPreview: (payload) => mockRequest<TestPreviewResult>('POST', '/api/test-mode/preview', payload),
  async addAllowlist(phone) {
    await mockRequest('POST', '/api/test-mode/allowlist', { phone });
  },
  async removeAllowlist(phone) {
    await mockRequest('DELETE', '/api/test-mode/allowlist?phone=' + encodeURIComponent(phone));
  },
  enterLive: (payload) => mockRequest<{ state: TestModeState; warnings: string[] }>('POST', '/api/test-mode/enter-live', payload),
  enterTest: () => mockRequest<TestModeState>('POST', '/api/test-mode/enter-test'),

  audit: (query) =>
    mockRequest<Page<AuditEvent>>(
      'GET',
      '/api/admin/audit?' + new URLSearchParams({ action: query.action ?? '', actor: query.actor ?? '', q: '' }).toString()
    ),
  async auditCsv(query) {
    const page = await mockRequest<Page<AuditEvent>>(
      'GET',
      '/api/admin/audit?' + new URLSearchParams({ action: query.action ?? '', actor: query.actor ?? '', q: '' }).toString()
    );
    const header = 'zaman,aktor,eylem,hedef,sonuc,ip';
    const lines = page.items.map((row) => [row.at, row.actor, row.action, row.target, row.result, row.ip].join(','));
    return { blob: new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' }), filename: 'audit-mock.csv' };
  },
  settings: () => mockRequest<PanelConfig>('GET', '/api/settings'),
  saveSettings: (config) => mockRequest<PanelConfig>('PUT', '/api/settings', config),

  uploadMedia: (file) => mockRequest<MediaUpload>('POST', '/api/media/upload', { name: file.name, size: file.size, mime: file.type }),

  totpSetup: () => mockRequest<TotpSetup>('POST', '/api/auth/totp/setup'),
  async totpEnable(code) {
    const res = await mockRequest<{ recoveryCodes: string[] }>('POST', '/api/auth/totp/enable', { code });
    return res.recoveryCodes;
  },
  async regenerateRecoveryCodes(code) {
    const res = await mockRequest<{ recoveryCodes: string[] }>('POST', '/api/auth/recovery-codes', { code });
    return res.recoveryCodes;
  }
};

export const api: ApiClient = MOCK_MODE ? mockClient : realClient;

/** Hangi ekran gerçek API'de var? (uçlar yayınlandıkça true yapılır) */
export const FEATURES = {
  tickets: true,
  faqs: true,
  library: true,
  aiSettings: true,
  audit: true,
  settings: true,
  wa: true
} as const;

export type FeatureKey = keyof typeof FEATURES;
